import { useCallback, useEffect, useState } from 'react';
import { message, Select, Button, Space, Typography, Alert, Modal } from 'antd';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ActivityCategoryInfo, ActivityGameMatchInfo, ActivityOutletContext, BracketDraftInfo } from '../../interface';
import {
    generateBracketDraft,
    getBracketDraft,
    getMatchBracket,
    publishBracketDraft,
    updateBracketDraft,
} from '../../apiBridge';
import {
    assignBracketParticipant,
    bracketParticipantName,
    formatBracketParticipantLabel,
    matchesWithDraftSlots,
} from './bracketBuilderUtils';
import { operatorBracketEmptyCopy, TournamentBracket } from './bracketView';
import EditMatchModal from './_editMatchModal';

const { Text, Title } = Typography;

async function readDraftResponse<T>(response?: Response | null): Promise<T> {
    if (!response) {
        throw new Error('No response from match service');
    }
    const body = await response.json();
    if (!response.ok || body?.error) {
        throw new Error(body?.error || `Request failed (${response.status})`);
    }
    return body as T;
}

const BracketApp: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const data = useOutletContext() as ActivityOutletContext;
    const { setHasUnsavedChanges, reloadData } = data;

    const [searchParams] = useSearchParams();
    const categoryFromUrl = searchParams.get('category')?.trim() || null;
    const defaultCategoryId = data.categories?.[0] ? String(data.categories[0].id) : null;
    const [nowCategory, setNowCategory] = useState<string | null>(
        categoryFromUrl || defaultCategoryId,
    );
    const navigate = useNavigate();

    const [loadingState, setLoadingState] = useState<boolean>(true);
    const [matchData, setMatchData] = useState<ActivityGameMatchInfo[]>([]);
    const [nowCategoryData, setNowCategoryData] = useState<ActivityCategoryInfo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [draft, setDraft] = useState<BracketDraftInfo | null>(null);
    const [savedMatches, setSavedMatches] = useState<ActivityGameMatchInfo[]>([]);
    const [workingSlots, setWorkingSlots] = useState<BracketDraftInfo['slots']>([]);
    const [editingSlots, setEditingSlots] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [editMatch, setEditMatch] = useState<ActivityGameMatchInfo | null>(null);

    const discardLocalSlotEdits = () => {
        setDirty(false);
        setHasUnsavedChanges?.(false);
        setWorkingSlots(draft?.slots || []);
        if (savedMatches.length) {
            setMatchData(savedMatches);
        }
    };

    const confirmIfDirty = (action: () => void) => {
        if (!dirty) {
            action();
            return;
        }
        Modal.confirm({
            title: 'Discard unsaved changes?',
            content: 'Your unsaved participant positions will be lost.',
            okText: 'Discard',
            okButtonProps: { danger: true },
            onOk: () => {
                discardLocalSlotEdits();
                action();
            },
        });
    };

    const handleViewMatch = () => {
        confirmIfDirty(() => {
            if (!nowCategory) {
                navigate(`/activity/${data.activity.id}/matches`);
                return;
            }
            navigate(`/activity/${data.activity.id}/matches?category=${nowCategory}`);
        });
    };

    const selectedCategory =
        nowCategoryData ||
        data.categories?.find(category => String(category.id) === String(nowCategory)) ||
        null;

    const handleManageBracket = () => {
        if (!nowCategory) {
            return;
        }
        if (selectedCategory?.need_reload) {
            messageApi.warning(
                'Participant information has changed. Please reload the category before editing the bracket.',
            );
            return;
        }
        if (selectedCategory && !selectedCategory.is_setup_done) {
            messageApi.warning('Finish Category Setup before creating or editing the bracket draft.');
            return;
        }
        confirmIfDirty(() => {
            navigate(`/activity/${data.activity.id}/bracket-builder/${nowCategory}`);
        });
    };

    const applyCategoryChange = (value: string) => {
        setMatchData([]);
        setSavedMatches([]);
        setDraft(null);
        setWorkingSlots([]);
        setDirty(false);
        setHasUnsavedChanges?.(false);
        setLoadingState(true);
        setErrorMessage(null);
        setEditingSlots(false);
        setNowCategory(value);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('category', value);
        window.history.pushState({}, '', `?${newSearchParams.toString()}`);
    };

    const handleCategoryChange = (value: string) => {
        confirmIfDirty(() => applyCategoryChange(value));
    };

    const loadMatches = useCallback(async () => {
        if (!nowCategory) {
            setMatchData([]);
            setSavedMatches([]);
            setWorkingSlots([]);
            setLoadingState(false);
            return;
        }

        try {
            const matches = await getMatchBracket(data.activity.id, nowCategory);
            if (!matches) {
                return;
            }

            const res = await matches.json();

            const category = data.categories?.find(cat => String(cat.id) === String(nowCategory)) || null;
            setNowCategoryData(category);

            if (res.error) {
                setErrorMessage(res.error);
                setMatchData([]);
                setSavedMatches([]);
                setDraft(null);
                setWorkingSlots([]);
                setDirty(false);
                setHasUnsavedChanges?.(false);
                return;
            }

            setErrorMessage(null);
            if (res.matches && res.matches.length > 0) {
                setMatchData(res.matches);
                setSavedMatches(res.matches);
                if (category && !category.is_bracket_done) {
                    const draftRes = await getBracketDraft(data.activity.id, nowCategory);
                    if (draftRes) {
                        const draftBody = await draftRes.json();
                        if (!draftBody.error) {
                            const nextDraft = draftBody as BracketDraftInfo;
                            setDraft(nextDraft);
                            setWorkingSlots(nextDraft.slots);
                        } else {
                            setDraft(null);
                            setWorkingSlots([]);
                        }
                    }
                } else {
                    setDraft(null);
                    setWorkingSlots([]);
                }
                setDirty(false);
                setHasUnsavedChanges?.(false);
            } else {
                setMatchData([]);
                setSavedMatches([]);
                setDraft(null);
                setWorkingSlots([]);
                setDirty(false);
                setHasUnsavedChanges?.(false);
            }
        } catch (error) {
            console.error('Error loading matches:', error);
            setErrorMessage('Failed to load bracket data');
        } finally {
            setLoadingState(false);
        }
    }, [data.activity.id, data.categories, nowCategory]);

    const assignableDraft = Boolean(
        draft &&
        draft.status !== 'published' &&
        !draft.competition_locked,
    );
    const canEditSlots = Boolean(
        assignableDraft &&
        selectedCategory &&
        !selectedCategory.need_reload &&
        selectedCategory.is_setup_done,
    );

    const saveSlotAssignments = async (
        nextSlots: BracketDraftInfo['slots'],
        expectedRevision: number,
    ) => {
        if (!nowCategory) {
            throw new Error('No category selected');
        }
        const assignments = nextSlots
            .filter(slot => slot.kind === 'participant')
            .map(slot => ({
                slot_key: slot.key,
                participant_id: slot.participant_id || '',
                is_seed_locked: Boolean(slot.is_seed_locked && slot.participant_id),
            }));
        return readDraftResponse<BracketDraftInfo>(
            await updateBracketDraft(
                data.activity.id,
                nowCategory,
                expectedRevision,
                assignments,
            ),
        );
    };

    const chooseSlotParticipant = (slotKey: string, participantId?: string) => {
        if (!canEditSlots || !draft) {
            return;
        }
        const nextSlots = assignBracketParticipant(workingSlots, slotKey, participantId);
        if (nextSlots === workingSlots) {
            if (participantId) {
                messageApi.warning('Locked positions cannot be moved');
            }
            return;
        }
        setWorkingSlots(nextSlots);
        setMatchData(matchesWithDraftSlots(savedMatches, nextSlots, draft.participants));
        setDirty(true);
        setHasUnsavedChanges?.(true);
    };

    const persistDraftOnly = async (
        slots: BracketDraftInfo['slots'],
        revision: number,
    ) => {
        if (!nowCategory) {
            return;
        }
        setAssigning(true);
        try {
            await saveSlotAssignments(slots, revision);
            setDirty(false);
            setHasUnsavedChanges?.(false);
            messageApi.success('Draft updated');
            await loadMatches();
        } catch (error: any) {
            messageApi.error(error.message || 'Unable to update bracket draft');
        } finally {
            setAssigning(false);
        }
    };

    const completePublish = async (
        slots: BracketDraftInfo['slots'],
        revision: number,
        randomizeUnassigned: boolean,
    ) => {
        if (!nowCategory) {
            return;
        }
        setAssigning(true);
        try {
            let nextRevision = revision;
            const saved = await saveSlotAssignments(slots, nextRevision);
            nextRevision = saved.revision;
            if (randomizeUnassigned) {
                const randomized = await readDraftResponse<BracketDraftInfo>(
                    await generateBracketDraft(
                        data.activity.id,
                        nowCategory,
                        nextRevision,
                        'unassigned',
                    ),
                );
                nextRevision = randomized.revision;
            }
            await readDraftResponse(
                await publishBracketDraft(data.activity.id, nowCategory, nextRevision),
            );
            setDirty(false);
            setHasUnsavedChanges?.(false);
            setEditingSlots(false);
            reloadData?.('');
            messageApi.success('Bracket published');
            await loadMatches();
        } catch (error: any) {
            messageApi.error(error.message || 'Unable to publish bracket');
        } finally {
            setAssigning(false);
        }
    };

    const persistAndPublish = (
        slots: BracketDraftInfo['slots'],
        revision: number,
        unassignedCount: number,
    ) => {
        if (unassignedCount > 0) {
            Modal.confirm({
                title: 'Randomize unassigned positions?',
                content: `${unassignedCount} participant position(s) are still unassigned and the bracket cannot be published until every position is filled. Randomly fill them and publish?`,
                okText: 'Randomize and publish',
                cancelText: 'Cancel',
                onOk: () => completePublish(slots, revision, true),
            });
            return;
        }
        void completePublish(slots, revision, false);
    };

    const saveSlotEdits = () => {
        if (!draft || !nowCategory || !dirty) {
            return;
        }
        const slots = workingSlots;
        const revision = draft.revision;
        const unassignedCount = slots.filter(
            slot => slot.kind === 'participant' && !slot.participant_id,
        ).length;
        if (draft.status === 'published') {
            void persistDraftOnly(slots, revision);
            return;
        }
        const modal = Modal.confirm({
            title: 'Publish this bracket?',
            content:
                'This bracket has not been published yet. Publish it now, or save it as a draft for now.',
            closable: true,
            footer: (
                <Space style={{ paddingTop: 16 }}>
                    <Button onClick={() => modal.destroy()}>Cancel</Button>
                    <Button
                        onClick={() => {
                            modal.destroy();
                            void persistDraftOnly(slots, revision);
                        }}
                    >
                        Save as draft
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            modal.destroy();
                            persistAndPublish(slots, revision, unassignedCount);
                        }}
                    >
                        Publish
                    </Button>
                </Space>
            ),
        });
    };

    const handleToggleEditing = () => {
        if (editingSlots) {
            confirmIfDirty(() => setEditingSlots(false));
            return;
        }
        setEditingSlots(true);
    };

    const handleOpenEdit = (match: ActivityGameMatchInfo) => {
        if (match.state === 'bye' || !match.bout_number) {
            return;
        }
        setEditMatch(match);
    };

    const closeEditMatch = () => {
        setEditMatch(null);
    };

    const afterEditMatch = () => {
        setEditMatch(null);
        loadMatches();
    };

    useEffect(() => {
        if (categoryFromUrl) {
            setNowCategory(categoryFromUrl);
            return;
        }
        if (!defaultCategoryId) {
            return;
        }
        setNowCategory(current => current || defaultCategoryId);
        const next = new URLSearchParams(window.location.search);
        if (next.get('category')) {
            return;
        }
        next.set('category', defaultCategoryId);
        const query = next.toString();
        window.history.replaceState({}, '', query ? `?${query}` : window.location.pathname);
    }, [categoryFromUrl, defaultCategoryId]);

    useEffect(() => {
        loadMatches();
    }, [loadMatches]);

    useEffect(() => {
        if (!canEditSlots) {
            setEditingSlots(false);
        }
    }, [canEditSlots]);

    const emptyCopy = operatorBracketEmptyCopy({
        selectedCategoryId: nowCategory,
        category: selectedCategory,
        error: errorMessage,
        hasMatches: matchData.length > 0,
    });

    return (
        <div style={{ padding: '20px' }}>
            {contextHolder}

            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Select
                    style={{ width: 300 }}
                    onChange={handleCategoryChange}
                    value={nowCategory}
                    options={data.categories?.map(category => ({
                        label: category.name,
                        value: category.id.toString(),
                        key: category.id.toString(),
                    }))}
                    placeholder="Select a category"
                    loading={!data.categories}
                />
                <Button type="primary" onClick={handleViewMatch}>
                    View Match Details
                </Button>
                {nowCategory ? (
                    <>
                        <Button
                            onClick={handleManageBracket}
                            disabled={Boolean(
                                selectedCategory &&
                                (selectedCategory.need_reload || !selectedCategory.is_setup_done),
                            )}
                        >
                            Manage Bracket
                        </Button>
                        <Button
                            onClick={handleToggleEditing}
                            disabled={!canEditSlots}
                        >
                            {editingSlots ? 'Done' : 'Edit'}
                        </Button>
                        {editingSlots ? (
                            <Button
                                type="primary"
                                onClick={saveSlotEdits}
                                disabled={!dirty}
                                loading={assigning}
                            >
                                Save
                            </Button>
                        ) : null}
                    </>
                ) : null}
            </div>

            {loadingState ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <p>Loading bracket data...</p>
                </div>
            ) : emptyCopy ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '300px',
                    textAlign: 'center',
                    padding: '20px',
                }}>
                    <Text style={{ fontSize: '16px', marginBottom: '16px' }}>
                        {emptyCopy.title}
                    </Text>
                    {emptyCopy.detail && (
                        <Text type="secondary">{emptyCopy.detail}</Text>
                    )}
                </div>
            ) : nowCategoryData && (
                <>
                    {editingSlots && canEditSlots && (
                        <Alert
                            type={dirty ? 'warning' : 'info'}
                            showIcon
                            banner
                            style={{ marginBottom: 12 }}
                            message={
                                dirty
                                    ? 'Unsaved changes. Save before leaving this page or switching category.'
                                    : 'Choose a name to swap first-round or preliminary positions. Locked seeds stay in place.'
                            }
                        />
                    )}
                    <TournamentBracket
                        matches={matchData}
                        roundNames={nowCategoryData.round_names}
                        hasExtraMatches={nowCategoryData.has_extra_matches}
                        layoutKey={nowCategoryData.id}
                        showScheduleMeta
                        onOpenEdit={match => {
                            const full = matchData.find(item => item.id === match.id);
                            if (full) {
                                handleOpenEdit(full);
                            }
                        }}
                        slotEditor={
                            editingSlots && canEditSlots && draft
                                ? {
                                    enabled: true,
                                    busy: assigning,
                                    optionsForSlot: (slotKey, currentParticipantId) =>
                                        draft.participants.map(participant => {
                                            const occupant = workingSlots.find(
                                                slot => slot.participant_id === participant.id,
                                            );
                                            const licenseNumber = participant.license_number?.trim();
                                            return {
                                                value: participant.id,
                                                searchLabel: formatBracketParticipantLabel(participant),
                                                label: (
                                                    <span className="bracket-view-participant-select-label">
                                                        <span>{bracketParticipantName(participant)}</span>
                                                        {licenseNumber ? (
                                                            <span className="bracket-view-participant-select-id">
                                                                {licenseNumber}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                ),
                                                disabled:
                                                    participant.id !== currentParticipantId &&
                                                    Boolean(occupant?.is_seed_locked),
                                            };
                                        }),
                                    onChoose: chooseSlotParticipant,
                                }
                                : undefined
                        }
                    />
                    <div style={{
                        background: '#fff',
                        padding: '20px',
                        margin: '20px 0',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}>
                        <Title level={5} style={{ marginBottom: '16px', color: '#1f1f1f' }}>Legend</Title>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '12px',
                        }}>
                            {[
                                ['RSC', 'Win by Referee Stops Contest'],
                                ['PTF', 'Win by final score'],
                                ['WDR', 'Win by Withdrawal'],
                                ['DSQ', 'Win by Disqualification'],
                                ['DQB', 'Win by Disqualification for unsportsmanlike behavior'],
                                ['WO', 'Walkover'],
                            ].map(([code, label]) => (
                                <div
                                    key={code}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 12px',
                                        background: '#fafafa',
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Text strong style={{ marginRight: '8px', color: '#1890ff', whiteSpace: 'nowrap' }}>{code}</Text>
                                    <Text>{label}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            <EditMatchModal
                visible={Boolean(editMatch)}
                onCancel={closeEditMatch}
                onOk={afterEditMatch}
                currentMatch={editMatch}
                onMatchReset={afterEditMatch}
                matchData={matchData}
            />
        </div>
    );
};

export default BracketApp;
